/**
 * Domain Normalization Service
 * 
 * Provides consistent domain name normalization logic for duplicate detection
 * and comparison operations. Ensures all domain names are processed uniformly
 * across the application.
 */
export class DomainNormalizationService {
  /**
   * Normalizes a domain name by trimming whitespace and converting to lowercase
   * 
   * @param domainName - The domain name to normalize
   * @returns The normalized domain name (trimmed and lowercase)
   */
  static normalizeDomainName(domainName: string): string {
    if (!domainName || typeof domainName !== 'string') {
      return '';
    }
    
    return domainName.trim().toLowerCase();
  }

  /**
   * Creates a mapping of original domain names to their normalized versions
   * 
   * @param domains - Array of original domain names
   * @returns Map with original domain names as keys and normalized names as values
   */
  static createNormalizedMap(domains: string[]): Map<string, string> {
    const map = new Map<string, string>();
    
    domains.forEach(domain => {
      if (typeof domain === 'string') {
        map.set(domain, this.normalizeDomainName(domain));
      }
    });
    
    return map;
  }

  /**
   * Creates a reverse mapping of normalized domain names to their original versions
   * Useful for looking up original domain names from normalized keys
   * 
   * @param domains - Array of original domain names
   * @returns Map with normalized domain names as keys and original names as values
   */
  static createReverseLookupMap(domains: string[]): Map<string, string> {
    const map = new Map<string, string>();
    
    domains.forEach(domain => {
      if (typeof domain === 'string') {
        const normalized = this.normalizeDomainName(domain);
        // If multiple domains normalize to the same value, keep the first one
        if (!map.has(normalized)) {
          map.set(normalized, domain);
        }
      }
    });
    
    return map;
  }

  /**
   * Checks if two domain names are equivalent after normalization
   * 
   * @param domain1 - First domain name
   * @param domain2 - Second domain name
   * @returns True if the domains are equivalent after normalization
   */
  static areDomainsEquivalent(domain1: string, domain2: string): boolean {
    return this.normalizeDomainName(domain1) === this.normalizeDomainName(domain2);
  }
}